import { useCallback, useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import CircleCheckboxInput from 'LuxKit/Checkbox/CircleCheckboxInput'
import Group from 'components/utils/Group'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineArrowUpRightIcon from 'LuxKit/Icons/line/LineArrowUpRightIcon'
import IconButton from 'LuxKit/Button/IconButton'
import noop from 'lib/function/noop'
import LockedOptionsList from './LockedOptionsList'
import { SelectableCard } from './SelectableCard'
import { useCardSelection } from './useCardSelection'
import {
  OptionDescription,
  OptionTitle,
  StreamingFrame,
  StreamingSkeletonCard,
  TextContent,
  TitleRow,
  TrailingControl,
} from './CardListStyles'
import StreamingCardContainer from './StreamingCardContainer'

export interface CardOption {
  title: string
  description?: string
  emoji?: string
  placeSearch?: string
  action?: string
}

export type CardCategory = 'generic' | 'places' | 'locations'
type LocationInfoCategory = Extract<CardCategory, 'places' | 'locations'>

const isLocationInfoCategory = (categories?: CardCategory): categories is LocationInfoCategory =>
  categories === 'locations' || categories === 'places'

export const canOpenLocationInfo = (option: CardOption, categories?: CardCategory) =>
  isLocationInfoCategory(categories) && Boolean(option.placeSearch?.trim())

const CardContent = styled(Group)`
  min-width: 0;

  @container chat-card-list (max-width: 480px) {
    column-gap: ${rem(10)};

    &.dir-horizontal {
      align-items: flex-start;
    }
  }
`

const EmojiText = styled(BodyText)`
  flex-shrink: 0;

  @container chat-card-list (max-width: 480px) {
    font-size: ${rem(20)};
    line-height: ${rem(24)};
  }
`

interface Props {
  options: Array<CardOption>
  onSelectionChange: (selectedTitles: Array<string>) => void
  select: 'single' | 'multiple'
  disabled?: boolean
  selection?: Array<string>
  categories?: CardCategory
  onInfoClick?: (option: CardOption, category: CardCategory) => void
  onAction?: (action: string) => boolean | void
  isStreaming?: boolean
}

function CardsList({
  options,
  onSelectionChange,
  select,
  disabled = false,
  selection,
  categories,
  onInfoClick = noop,
  onAction,
  isStreaming = false,
}: Props) {
  const { selectedIndices, optionClickHandlers } = useCardSelection({
    options,
    selection,
    disabled,
    select,
    onSelectionChange,
    onAction,
  })

  const onInfoClickRef = useRef(onInfoClick)
  onInfoClickRef.current = onInfoClick

  const handleInfoClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    const indexStr = event.currentTarget.dataset.optionIndex
    if (indexStr === undefined) return
    const index = parseInt(indexStr, 10)
    if (isNaN(index)) return
    const option = options[index]
    if (option && isLocationInfoCategory(categories) && canOpenLocationInfo(option, categories)) {
      onInfoClickRef.current(option, categories)
    }
  }, [options, categories])

  const getOptionKey = useCallback((option: CardOption, index: number) => `${option.title}-${index}`, [])

  const renderOption = useCallback(({ option, index, isSelected }: { option: CardOption; index: number; isSelected: boolean }) => (
    <StreamingCardContainer isStreaming={isStreaming}>
      <SelectableCard
        role={select === 'single' ? 'radio' : 'checkbox'}
        aria-checked={isSelected}
        disabled={disabled}
        onClick={optionClickHandlers[index]}
        className={isSelected ? 'selected' : undefined}
      >
        <CardContent direction="horizontal" gap={16} fullWidth verticalAlign="center">
          {option.emoji && (
            <EmojiText variant="large" colour="neutral-one" weight="bold">
              {option.emoji}
            </EmojiText>
          )}
          <TextContent direction="vertical" gap={2} fullWidth>
            <TitleRow direction="horizontal" gap={4} verticalAlign="center">
              <OptionTitle variant="medium" colour="neutral-one" weight="bold">
                {option.title}
              </OptionTitle>
              {canOpenLocationInfo(option, categories) && (
                <IconButton
                  kind="tertiary"
                  size="small"
                  data-option-index={index}
                  onClick={handleInfoClick}
                >
                  <LineInfoCircleIcon />
                </IconButton>
              )}
            </TitleRow>
            {option.description && (
              <OptionDescription variant="medium" colour="neutral-two">
                {option.description}
              </OptionDescription>
            )}
          </TextContent>
          {select === 'multiple' && (
            <TrailingControl>
              <CircleCheckboxInput
                checked={isSelected}
                disabled={disabled}
                aria-hidden="true"
              />
            </TrailingControl>
          )}
          {(option.action === 'call_expert' || option.action === 'schedule_callback') && (
            <TrailingControl>
              <LineArrowUpRightIcon />
            </TrailingControl>
          )}
        </CardContent>
      </SelectableCard>
    </StreamingCardContainer>
  ), [categories, disabled, handleInfoClick, isStreaming, optionClickHandlers, select])

  if (isStreaming && options.length === 0) {
    return (
      <StreamingFrame aria-busy="true" aria-live="polite" data-testid="cards-list-streaming-frame">
        <StreamingSkeletonCard data-testid="cards-list-streaming-skeleton">
          <CardContent direction="horizontal" gap={16} fullWidth verticalAlign="center">
            <TextContent direction="vertical" gap={8} fullWidth>
              <TextLoadingBox typography="body-medium" width="58%" />
              <TextLoadingBox typography="body-medium" width="82%" />
            </TextContent>
          </CardContent>
        </StreamingSkeletonCard>
      </StreamingFrame>
    )
  }

  return (
    <LockedOptionsList
      options={options}
      disabled={disabled}
      selectedIndices={selectedIndices}
      getOptionKey={getOptionKey}
      renderOption={renderOption}
    />
  )
}

export default CardsList
